import { useCallback } from 'react';
import {
  archiveQuoteDocumentToGed,
  downloadQuoteDocument,
  generateOfficialQuoteDocument,
  getQuoteVersion,
  previewQuoteDocument,
} from '../quote.api';
import { editorFromQuote } from '../quoteEditorModel';
import { buildQuoteGedArchiveRequest } from '../quoteDocumentModel';
import useQuoteEditorOperation from './useQuoteEditorOperation';

export default function useQuoteDocuments({
  editor,
  folder,
  setEditor,
  setSaving,
  setError,
  setStatusMessage,
  loadFolder,
  editorOperation,
}) {
  const ownOperation = useQuoteEditorOperation({ editor, setEditor, setSaving, setError, setStatusMessage });
  const operation = editorOperation || ownOperation;
  const previewDocument = useCallback(async (documentType) => {
    if (!editor?.id || !editor.quoteVersionId) return;
    const pending = operation.begin('prévisualisation');
    if (!pending) return;
    const previewWindow = typeof window !== 'undefined' ? window.open('', '_blank') : null;
    if (previewWindow) previewWindow.opener = null;
    try {
      const preview = await previewQuoteDocument(editor.id, editor.quoteVersionId, documentType);
      operation.succeeded(pending);
      if (!operation.selected(pending)) { if (previewWindow) previewWindow.close(); return; }
      if (preview?.id) {
        const blob = await downloadQuoteDocument(editor.id, editor.quoteVersionId, preview.id);
        if (!operation.selected(pending)) { if (previewWindow) previewWindow.close(); return; }
        const url = URL.createObjectURL(blob);
        if (previewWindow) previewWindow.location.href = url;
        else {
          const link = window.document.createElement('a');
          link.href = url; link.target = '_blank'; link.rel = 'noopener noreferrer'; link.click();
        }
        window.setTimeout(() => URL.revokeObjectURL(url), 60000);
      }
      const quote = await getQuoteVersion(editor.id, editor.quoteVersionId, folder);
      if (!operation.selected(pending)) return;
      if (operation.apply(pending, editorFromQuote(quote))) setStatusMessage('Prévisualisation générée et tracée.');
      if (folder && operation.selected(pending)) await loadFolder(editor.id);
    } catch (error) {
      if (previewWindow) previewWindow.close();
      operation.fail(pending, error);
    } finally {
      operation.finish(pending);
    }
  }, [
    editor, folder, loadFolder, setStatusMessage, operation,
  ]);

  const generateOfficialDocument = useCallback(async (documentType = 'client_quote') => {
    if (!editor?.id || !editor.quoteVersionId) return;
    if (!['issued', 'accepted'].includes(editor.versionStatus)) {
      setError('Validez puis émettez cette version avant de générer un document officiel. Le brouillon peut être prévisualisé.');
      return;
    }
    const pending = operation.begin('génération officielle');
    if (!pending) return;
    try {
      await generateOfficialQuoteDocument(editor.id, editor.quoteVersionId, documentType);
      operation.succeeded(pending);
      if (!operation.selected(pending)) return;
      const quote = await getQuoteVersion(editor.id, editor.quoteVersionId, folder);
      if (!operation.selected(pending)) return;
      if (operation.apply(pending, editorFromQuote(quote))) setStatusMessage('Document officiel généré depuis la version déjà émise.');
      if (folder && operation.selected(pending)) await loadFolder(editor.id);
    } catch (error) {
      operation.fail(pending, error);
    } finally {
      operation.finish(pending);
    }
  }, [editor, folder, loadFolder, setError, setStatusMessage, operation]);

  const archiveDocumentToGed = useCallback(async (document) => {
    if (!editor?.id || !editor.quoteVersionId || !document?.id) return;
    const pending = operation.begin('archivage GED');
    if (!pending) return;
    try {
      await archiveQuoteDocumentToGed(
        editor.id,
        editor.quoteVersionId,
        document.id,
        buildQuoteGedArchiveRequest(editor, document)
      );
      operation.succeeded(pending);
      if (!operation.selected(pending)) return;
      const quote = await getQuoteVersion(editor.id, editor.quoteVersionId, folder);
      if (!operation.selected(pending)) return;
      if (operation.apply(pending, editorFromQuote(quote))) setStatusMessage('Document archivé dans la GED.');
      if (folder && operation.selected(pending)) await loadFolder(editor.id);
    } catch (error) {
      operation.fail(pending, error);
    } finally {
      operation.finish(pending);
    }
  }, [editor, folder, loadFolder, setStatusMessage, operation]);

  const downloadDocument = useCallback(async (document) => {
    try {
      setSaving(true);
      const blob = await downloadQuoteDocument(
        editor.id,
        editor.quoteVersionId,
        document.id
      );
      const url = URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = document.fileName || 'document';
      link.click();
      URL.revokeObjectURL(url);
      if (folder) await loadFolder(editor.id);
    } catch (error) {
      setError(error.message || 'Impossible de télécharger le document');
    } finally {
      setSaving(false);
    }
  }, [editor, folder, loadFolder, setError, setSaving]);

  return { previewDocument, generateOfficialDocument, archiveDocumentToGed, downloadDocument };
}
